import axios from 'axios';

/**
 * Shape of the backend error body produced by `AIXONE.RestErrorHandling`
 * (`ErrorResponse`). The `message` is a template that may contain
 * `{{PlaceholderName}}` tokens which are resolved against `additionalData`
 * (the error's metadata). The token names and the metadata keys are both the
 * PascalCase C# property names, so they match verbatim.
 */
export interface ApiErrorResponse {
  errorCode?: string | number;
  message?: string;
  additionalData?: Record<string, unknown> | null;
  traceId?: string;
}

/**
 * Returns the backend `ErrorResponse` carried by an axios error, or
 * `undefined` when the error is not an axios error with a recognizable body.
 */
export const extractApiErrorResponse = (error: unknown): ApiErrorResponse | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }
  const data = error.response?.data as ApiErrorResponse | undefined;
  return data && typeof data.message === 'string' ? data : undefined;
};

/**
 * Replaces `{{Key}}` tokens (i18next double-brace syntax, optionally padded
 * with whitespace) in the message with the matching value from
 * `additionalData`. Unknown tokens are left untouched so a missing value is
 * visible rather than silently dropped.
 */
const interpolate = (template: string, data?: Record<string, unknown> | null): string =>
  template.replace(/{{\s*([\w.]+)\s*}}/g, (match, key: string) => {
    const value = data?.[key];
    return value === undefined || value === null ? match : String(value);
  });

/**
 * Resolves the human-readable message for an error. When the backend returned
 * a structured `ErrorResponse`, its templated message is interpolated with the
 * error metadata; otherwise the axios/native message is used, falling back to
 * the provided default.
 */
export const resolveApiErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = extractApiErrorResponse(error);
  if (apiError?.message) {
    return interpolate(apiError.message, apiError.additionalData);
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};
